import {
  DollarOutlined,
  TableOutlined,
  FundOutlined,
  ApartmentOutlined,
  DeploymentUnitOutlined,
  GlobalOutlined,
  WalletOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  BankOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  AuditOutlined,
  SettingOutlined,
  CalculatorOutlined,
  ApiOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  CustomerServiceOutlined,
  StockOutlined,
} from '@ant-design/icons';
import FactoryOutlined from '../components/icons/FactoryOutlined';

/**
 * The sidebar, in four categories.
 *
 * A category is a heading, not a destination: it has no path and no permission
 * of its own, and it exists only if the caller can see at least one page under
 * it. Deriving that from the children rather than giving categories their own
 * permission means there is one fact to maintain per page instead of two that
 * can disagree — a category granted but empty, or an accessible page hidden
 * behind a category that was not.
 *
 * User Management sits outside the four. It is administration of the tool
 * rather than a view of the business, it is already gated to SUPER and ADMIN,
 * and burying an admin's only route to it under a business heading would be
 * the wrong kind of tidy.
 */
const menuItems = [
  {
    key: 'accounts',
    icon: DollarOutlined,
    label: 'Accounts',
    children: [
      {
        key: 'cashAccount',
        icon: BankOutlined,
        label: 'Cash Accounts',
        path: '/cash-account',
        permission: 'page:cash-account',
      },
      {
        key: 'xeroPL',
        icon: FileTextOutlined,
        label: 'Xero Financial Report',
        path: '/xero-pl',
        permission: 'page:xero-pl',
      },
      {
        key: 'liabilityList',
        icon: AuditOutlined,
        label: 'Liability List',
        path: '/liability-list',
        permission: 'page:liability-list',
      },
      {
        key: 'budgetControl',
        icon: WalletOutlined,
        label: 'Opex Control',
        // The path and the permission keep their old names. Both are already in
        // circulation — one in people's bookmarks, the other in every role that
        // has been granted the page — and renaming a permission key is how you
        // silently revoke access from everybody holding it.
        path: '/budget-control',
        permission: 'page:budget-control',
      },
      {
        key: 'cashflowControl',
        icon: FundOutlined,
        label: 'Cashflow Control',
        path: '/cashflow-control',
        // Its own permission, not Opex Control's: that page is open to supplier
        // owners so they can approve their own vendors' bills, and the group's
        // cash position is a different thing to be allowed to see.
        permission: 'page:cashflow-control',
      },
      {
        key: 'monthlyFinance',
        icon: BarChartOutlined,
        label: 'Monthly Finance',
        path: '/monthly-finance',
        permission: 'page:monthly-finance',
      },
      {
        key: 'financialPlanning',
        icon: LineChartOutlined,
        label: 'Financial Planning',
        path: '/financial-planning',
        permission: 'page:monthly-finance',
      },
      {
        key: 'projectFinance',
        icon: CalculatorOutlined,
        label: 'Project Finance',
        path: '/project-finance',
        permission: 'page:project-finance',
      },
    ],
  },
  {
    key: 'operations',
    icon: ApartmentOutlined,
    label: 'Operations',
    children: [
      {
        key: 'retailFinance',
        icon: HomeOutlined,
        label: 'SH Residential',
        path: '/retail-finance',
        permission: 'page:retail-finance',
      },
      {
        key: 'commercialFinance',
        icon: FactoryOutlined,
        label: 'SH Commercial',
        path: '/commercial-finance',
        // Same gate as SH Residential — no separate permission
        permission: 'page:retail-finance',
      },
      {
        key: 'complaints',
        icon: CustomerServiceOutlined,
        label: 'Customer Service',
        path: '/complaints',
        permission: 'page:complaints',
      },
      {
        key: 'distribution',
        icon: ShoppingCartOutlined,
        label: 'PW Renewable',
        path: '/distribution',
        permission: 'page:distribution',
      },
      {
        key: 'ppaSystem',
        icon: ThunderboltOutlined,
        label: 'PPA System',
        path: '/ppa',
        permission: 'page:ppa',
      },
      {
        key: 'vppRevenue',
        icon: LineChartOutlined,
        label: 'VPP Revenue',
        path: '/vpp',
        permission: 'page:vpp',
      },
      {
        key: 'powerTracking',
        icon: ApiOutlined,
        label: 'Powow Power',
        path: '/power-tracking',
        // Either permission opens the page; which tabs appear inside it is decided
        // there. Roles given the assistant alone still need a way in.
        anyPermission: ['page:power-tracking', 'page:power-ai'],
      },
    ],
  },
  {
    key: 'generations',
    icon: DeploymentUnitOutlined,
    label: 'Generations',
    children: [
      {
        key: 'mapOfSystems',
        icon: EnvironmentOutlined,
        label: 'Map of Systems',
        path: '/map-of-systems',
        permission: 'page:map-of-systems',
      },
    ],
  },
  {
    key: 'market',
    icon: GlobalOutlined,
    label: 'Market',
    /*
     * Open to anyone who can sign in.
     *
     * These three read public market data — certificate registrations, AEMO
     * dispatch, FCAS enablement. None of it is ours, none of it is commercial
     * in confidence, and gating it meant the Market heading expanded into a
     * different set of pages for every reader. The permissions still exist in
     * the database so the gate can be put back without a migration; they are
     * simply not consulted.
     */
    children: [
      {
        key: 'marketIntelligence',
        icon: SafetyCertificateOutlined,
        label: 'Market Intelligence',
        path: '/market-intelligence',
      },
      {
        key: 'aemoEnergyMarket',
        icon: StockOutlined,
        label: 'AEMO Energy Market',
        path: '/aemo-energy-market',
      },
      {
        key: 'fcasMarket',
        icon: ThunderboltOutlined,
        label: 'FCAS Market',
        path: '/fcas-market',
      },
      {
        key: 'rebateCheatsheet',
        icon: TableOutlined,
        label: 'Rebate Cheatsheet',
        path: '/rebate-cheatsheet',
      },
    ],
  },
  {
    key: 'userManagement',
    icon: SettingOutlined,
    label: 'User Management',
    path: '/admin/users',
    permission: 'page:users',
    requiredRoleTypes: ['SUPER', 'ADMIN'],
  },
];

/**
 * May this caller see this one entry?
 *
 * Exported because the sidebar and the login landing page both have to answer
 * it, and they used to answer it differently: the landing page looked only at
 * `permission`, so somebody holding page:power-ai alone — which reaches Powow
 * Power through `anyPermission` — was sent to their profile past a page they
 * could in fact open.
 */
export function canSee(item, permissions = [], roleType) {
  if (item.requiredRoleTypes && !item.requiredRoleTypes.includes(roleType)) return false;
  if (item.permission && !permissions.includes(item.permission)) return false;
  if (item.anyPermission && !item.anyPermission.some((p) => permissions.includes(p))) return false;
  return true;
}

/**
 * The menu this caller actually gets: pages they hold, and only the categories
 * that still have a page left in them.
 */
export function visibleMenu(permissions = [], roleType) {
  const out = [];
  for (const item of menuItems) {
    if (!canSee(item, permissions, roleType)) continue;
    if (!item.children) { out.push(item); continue; }
    const children = item.children.filter((c) => canSee(c, permissions, roleType));
    // An empty category is not a category. Nothing behind the heading means
    // nothing to say, so it does not appear at all.
    if (children.length) out.push({ ...item, children });
  }
  return out;
}

/** Every page, category headings dropped — for anything that wants a flat list. */
export function visiblePages(permissions = [], roleType) {
  return visibleMenu(permissions, roleType)
    .flatMap((item) => (item.children ? item.children : [item]))
    .filter((item) => item.path);
}

/** Which category a page sits in, so the sidebar can open the right one. */
export function categoryOf(pageKey) {
  const parent = menuItems.find(
    (item) => item.children && item.children.some((c) => c.key === pageKey),
  );
  return parent ? parent.key : null;
}

export default menuItems;
